import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";

import {
     cxGetWinnerFilterOptionsRequest,
     cxGetWinnersRequest,
} from "../../../../../_redux/_actions";
// Imported from its own file rather than the _constants barrel: the barrel can still be
// half-initialised when this module evaluates, which leaves the constants undefined.
import { cxAlwaysInTouchConstants } from "../../../../../_redux/_constants/cxAlwaysInTouch.constants";
import { monthDisplayName } from "../../cxAwardMonth";

const ALL_OPTION = { id: null, name: "All" };

const ACTIVATION_OPTIONS = [
     { id: null, name: "All" },
     { id: true, name: "Activated" },
     { id: false, name: "Not activated" },
];

const INITIAL_FILTERS = {
     activation: ACTIVATION_OPTIONS[0],
     area: ALL_OPTION,
     dealer: ALL_OPTION,
     month: ALL_OPTION,
};

const INITIAL_PAGE = { skip: 0, take: 10 };

const CxWinnersGrid = () => {
     const actionDispatch = useDispatch();
     const winnersPage = useSelector(
          (state) => state?.cxAlwaysInTouch?.winnerList,
     );
     const winnerFilterOptions = useSelector(
          (state) => state?.cxAlwaysInTouch?.winnerFilterOptions,
     );

     // The winners call is in flight while its request action sits in the pending list the loading
     // middleware maintains. Used to mask the grid so a filter change never looks like it did nothing.
     const isLoadingWinners = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               cxAlwaysInTouchConstants.CX_GET_WINNERS_REQUEST,
          ),
     );

     const [filters, setFilters] = useState(INITIAL_FILTERS);
     const [search, setSearch] = useState("");
     const [page, setPage] = useState(INITIAL_PAGE);
     const [sort, setSort] = useState([]);
     const [optionSearch, setOptionSearch] = useState({
          dealerSearch: "",
          areaSearch: "",
     });

     // The dropdowns are searchable and the typed text is matched by the backend, so the option
     // lists are re-fetched rather than narrowed here.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    cxGetWinnerFilterOptionsRequest({
                         dealerSearch: optionSearch.dealerSearch || undefined,
                         areaSearch: optionSearch.areaSearch || undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, optionSearch]);

     // Filtering, sorting and paging are all done by the backend: the request carries the criteria
     // and the response is exactly the page that should be shown.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    cxGetWinnersRequest({
                         accountActivated: filters.activation.id,
                         areaId: filters.area.id,
                         dealershipId: filters.dealer.id,
                         month: filters.month.id || undefined,
                         searchTerm: search.trim() ? search.trim() : undefined,
                         page: Math.floor(page.skip / page.take) + 1,
                         pageSize: page.take,
                         sortBy: sort.length ? sort[0].field : undefined,
                         sortDirection: sort.length ? sort[0].dir : undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, filters, search, page, sort]);

     const areaOptions = useMemo(
          () => [ALL_OPTION, ...(winnerFilterOptions?.areas || [])],
          [winnerFilterOptions],
     );

     const dealerOptions = useMemo(
          () => [ALL_OPTION, ...(winnerFilterOptions?.dealers || [])],
          [winnerFilterOptions],
     );

     // Months come back as stored labels already in date order, so they are wrapped in the same
     // {id, name} shape the other dropdowns use. Only months that have an award are offered.
     const monthOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(winnerFilterOptions?.months || []).map((month) => ({
                    id: month,
                    name: monthDisplayName(month) || month,
               })),
          ],
          [winnerFilterOptions],
     );

     const winners = useMemo(() => {
          const list = winnersPage?.winners || [];

          return list.map((winner) => ({
               ...winner,
               name: `${winner.firstName || ""} ${winner.lastName || ""}`.trim(),
               monthName: monthDisplayName(winner.month) || winner.month || "",
               rewardValue:
                    winner.rewardValueGbp || winner.rewardValueGbp === 0
                         ? `GBP ${Number(winner.rewardValueGbp).toFixed(2)}`
                         : "",
               points: winner.pointsCredited
                    ? Number(winner.pointsCredited).toLocaleString()
                    : "",
               activationStatus: winner.accountActivated
                    ? "Activated"
                    : "Not activated",
               paidOn: winner.rewardPaidOn
                    ? moment(winner.rewardPaidOn).format("DD/MM/YYYY")
                    : "",
          }));
     }, [winnersPage]);

     const totalCount = winnersPage?.totalCount || 0;

     const onFilterChange = useCallback((field, value) => {
          setFilters((previous) => ({ ...previous, [field]: value }));
          setPage(INITIAL_PAGE);
     }, []);

     const onSearchChange = useCallback((event) => {
          setSearch(event.value);
          setPage(INITIAL_PAGE);
     }, []);

     const onOptionSearchChange = useCallback((field, event) => {
          setOptionSearch((previous) => ({
               ...previous,
               [field]: event.filter?.value || "",
          }));
     }, []);

     const clearFilters = useCallback(() => {
          setFilters(INITIAL_FILTERS);
          setSearch("");
          setPage(INITIAL_PAGE);
          setSort([]);
          setOptionSearch({ dealerSearch: "", areaSearch: "" });
     }, []);

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <Input
                         style={{ width: "260px" }}
                         value={search}
                         onChange={onSearchChange}
                         placeholder="Search name, email or dealer"
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Activation"
                         data={ACTIVATION_OPTIONS}
                         textField="name"
                         dataItemKey="name"
                         value={filters.activation}
                         onChange={(event) =>
                              onFilterChange("activation", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "150px" }}
                         label="Area"
                         data={areaOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.area}
                         filterable={true}
                         onFilterChange={(event) =>
                              onOptionSearchChange("areaSearch", event)
                         }
                         onChange={(event) => onFilterChange("area", event.value)}
                    />
                    <DropDownList
                         style={{ width: "280px" }}
                         label="Dealer"
                         data={dealerOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.dealer}
                         filterable={true}
                         onFilterChange={(event) =>
                              onOptionSearchChange("dealerSearch", event)
                         }
                         onChange={(event) =>
                              onFilterChange("dealer", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Month"
                         data={monthOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.month}
                         onChange={(event) =>
                              onFilterChange("month", event.value)
                         }
                    />
                    <Button type="button" onClick={clearFilters}>
                         Clear
                    </Button>
               </div>
               <div style={{ position: "relative" }}>
                    {isLoadingWinners && (
                         <div className="k-loading-mask" style={{ zIndex: 10 }}>
                              <span className="k-loading-text">Loading</span>
                              <div className="k-loading-image" />
                              <div className="k-loading-color" />
                         </div>
                    )}
                    <Grid
                         data={winners}
                         skip={page.skip}
                         take={page.take}
                         total={totalCount}
                         sort={sort}
                         sortable={true}
                         fixedScroll={true}
                         className="sba-area-data-grid"
                         pageable={{
                              buttonCount: 4,
                              pageSizes: [10, 25, 50],
                         }}
                         onPageChange={(event) => setPage(event.page)}
                         onSortChange={(event) => {
                              setSort(event.sort);
                              setPage(INITIAL_PAGE);
                         }}
                    >
                         <GridColumn field="name" title="Winner" width="200px" />
                         <GridColumn field="email" title="Email" width="260px" />
                         <GridColumn
                              field="dealerName"
                              title="Dealer"
                              width="240px"
                         />
                         <GridColumn field="area" title="Area" width="120px" />
                         <GridColumn field="role" title="Role" width="180px" />
                         <GridColumn
                              field="monthName"
                              title="Month"
                              width="160px"
                         />
                         <GridColumn
                              field="rewardValue"
                              title="Reward"
                              width="130px"
                              sortable={false}
                         />
                         <GridColumn
                              field="paidOn"
                              title="Paid"
                              width="140px"
                              sortable={false}
                         />
                         <GridColumn
                              field="activationStatus"
                              title="Activation"
                              width="160px"
                         />
                    </Grid>
               </div>
          </div>
     );
};

export default CxWinnersGrid;
